import React from "react";
import DynamicFormCard from "../forms/DynamicFormCard";
import CreatedFileCard from "../files/CreatedFileCard";
import {
  normalizeChatUiPolicy,
  parseHumanTakeoverMarker,
  parseContextCompactedMarker,
  isDynamicFormTool,
  isCreatedFileTool,
  parseSupervisorDecisionContent,
} from "./conversationUtils";
import {
  SystemMessage,
  HumanTakeoverMarker,
  ContextCompactedMarker,
  FormSubmissionMessage,
} from "./SystemMarkers";
import {
  WorkflowMessage,
  WorkflowResponseMessage,
  InterAgentMessage,
} from "./InterAgentMessages";
import {
  SendMessageCard,
  UserMessage,
  AgentMessage,
  SupervisorDecisionMessage,
} from "./MessageBubbles";
import { ScheduleMessageCard, RecurringScheduleCard, ToolCallCard } from "./ToolCards";
import { DateDivider, ThinkingIndicator } from "./ActivityIndicators";

function renderTypedAssistantParts(
  typedMessage,
  keyPrefix,
  agentName,
  agentId,
  grouped,
  onOpenAgentSettings,
  onOpenDynamicForm,
  onOpenCreatedFile,
  showToolCallsAndOutputsInChat,
  showSchedulingActivityInChat,
  showInterAgentMessagesInChat,
  respondedCorrelationIds,
  scheduledMessages,
  onEditScheduledMessage,
  onCancelScheduledMessage,
  onRefreshScheduledMessages,
  compactToolCalls,
) {
  const elements = [];
  const parts = typedMessage.parts || [];
  for (let p = 0; p < parts.length; p++) {
    const part = parts[p];
    const partKey = `${keyPrefix}-part-${p}`;
    if (part.type === "text") {
      const textContent = part.text || "";
      if (!textContent) continue;
      elements.push(
        <AgentMessage
          key={partKey}
          content={textContent}
          agentName={agentName}
          agentId={agentId}
          timestamp={typedMessage.createdAt || typedMessage.updatedAt}
          grouped={p > 0}
          isStreaming={part.state === "streaming"}
          onOpenAgentSettings={onOpenAgentSettings}
        />
      );
    } else if (part.type === "tool") {
      const toolName = part.toolName || "";
      if (
        !showToolCallsAndOutputsInChat &&
        !isDynamicFormTool(toolName) &&
        !isCreatedFileTool(toolName)
      ) continue;
      if (toolName === "review_task") continue;
      if (!showSchedulingActivityInChat && (toolName === "schedule_message" || toolName === "recurring_schedule")) continue;
      if (!showInterAgentMessagesInChat && toolName === "send_message") continue;
      let parsedInput = part.input;
      if (typeof parsedInput === "string") {
        try { parsedInput = JSON.parse(parsedInput); } catch (_) {}
      }
      const inputStr =
        typeof parsedInput === "object" && parsedInput !== null
          ? JSON.stringify(parsedInput, null, 2)
          : part.inputText || "";
      const resultStr =
        part.output !== undefined && part.output !== null
          ? typeof part.output === "string"
            ? part.output
            : JSON.stringify(part.output, null, 2)
          : part.errorText || null;
      const lifecycleState = part.state;

      if (isDynamicFormTool(toolName)) {
        elements.push(
          <DynamicFormCard
            key={partKey}
            toolName={toolName}
            args={inputStr}
            result={resultStr}
            agentName={agentName}
            agentId={agentId}
            grouped={false}
            onOpenForm={onOpenDynamicForm}
            onOpenAgentSettings={onOpenAgentSettings}
            lifecycleState={lifecycleState}
          />
        );
      } else if (isCreatedFileTool(toolName)) {
        elements.push(
          <CreatedFileCard
            key={partKey}
            toolName={toolName}
            args={inputStr}
            result={resultStr}
            agentName={agentName}
            agentId={agentId}
            grouped={false}
            onOpenFile={onOpenCreatedFile}
            onOpenAgentSettings={onOpenAgentSettings}
            lifecycleState={lifecycleState}
          />
        );
      } else if (toolName === "send_message") {
        let sendMsgResponseReq = null;
        let sendMsgCorrelationId = null;
        if (parsedInput && typeof parsedInput === "object") {
          sendMsgResponseReq = parsedInput.responseRequirement;
        }
        const outputObj = part.output;
        if (outputObj && typeof outputObj === "object") {
          if (!sendMsgResponseReq) sendMsgResponseReq = outputObj.responseRequirement;
          sendMsgCorrelationId = outputObj.correlationId;
        }
        const awaitingResponse =
          sendMsgResponseReq === "REQUIRED" &&
          sendMsgCorrelationId &&
          respondedCorrelationIds &&
          !respondedCorrelationIds.has(sendMsgCorrelationId);
        elements.push(
          <SendMessageCard
            key={partKey}
            args={inputStr}
            result={resultStr}
            agentName={agentName}
            agentId={agentId}
            grouped={false}
            awaitingResponse={awaitingResponse}
            onOpenAgentSettings={onOpenAgentSettings}
            lifecycleState={lifecycleState}
          />
        );
      } else if (toolName === "schedule_message") {
        elements.push(
          <ScheduleMessageCard
            key={partKey}
            args={inputStr}
            result={resultStr}
            agentName={agentName}
            agentId={agentId}
            grouped={false}
            lifecycleState={lifecycleState}
            scheduledMessages={scheduledMessages}
            onEditScheduledMessage={onEditScheduledMessage}
            onCancelScheduledMessage={onCancelScheduledMessage}
            onRefreshScheduledMessages={onRefreshScheduledMessages}
          />
        );
      } else if (toolName === "recurring_schedule") {
        elements.push(
          <RecurringScheduleCard
            key={partKey}
            args={inputStr}
            result={resultStr}
            agentName={agentName}
            agentId={agentId}
            grouped={false}
            lifecycleState={lifecycleState}
          />
        );
      } else {
        elements.push(
          <ToolCallCard
            key={partKey}
            toolName={toolName}
            args={inputStr}
            result={resultStr}
            agentName={agentName}
            agentId={agentId}
            grouped={false}
            onOpenAgentSettings={onOpenAgentSettings}
            lifecycleState={lifecycleState}
            compact={compactToolCalls}
          />
        );
      }
    }
  }
  return elements;
}

export function buildConversationMessages({
  messages,
  session,
  defaultAgentId,
  defaultAgentName,
  chatUiPolicy,
  scheduledMessages = null,
  onEditScheduledMessage = null,
  onCancelScheduledMessage = null,
  onRefreshScheduledMessages = null,
  onOpenDynamicForm = null,
  onOpenCreatedFile = null,
  onOpenAgentSettings = null,
  chatStatus = 'ready',
  streamToolLabel = null,
}) {
  if (!Array.isArray(messages)) {
    return { rendered: [], lastRole: null, lastSenderKey: null };
  }

  const rendered = [];
  let prevRole = null;
  let prevSenderKey = null;
  let lastDateKey = null;
  let firstUserMessageSeen = false;
  const isWorkflowSource = session?.source === "WORKFLOW_INVOCATION";
  const {
    showSystemPromptInChat,
    showToolCallsAndOutputsInChat,
    showInterAgentMessagesInChat,
    showWorkflowProvenanceInChat,
    showSchedulingActivityInChat,
    showSupervisorControlMessagesInChat,
    forceUserNameAsYou,
    compactToolCalls,
    publicUserDisplayName,
  } = normalizeChatUiPolicy(chatUiPolicy);
  const useWorkflowProvenanceCards =
    isWorkflowSource && showWorkflowProvenanceInChat;

  const respondedCorrelationIds = new Set();
  for (const msg of messages) {
    if (msg.role === "user" && msg.senderAgentId && msg.correlationId) {
      respondedCorrelationIds.add(msg.correlationId);
    }
  }

  const maybeInsertDateDivider = (timestamp, keyPrefix) => {
    if (!timestamp) return;
    const dateKey = new Date(timestamp).toDateString();
    if (dateKey !== lastDateKey) {
      rendered.push(
        <DateDivider key={`date-${keyPrefix}`} dateStr={timestamp} />
      );
      lastDateKey = dateKey;
      prevRole = null;
      prevSenderKey = null;
    }
  };

  const isInternalRuntimeSystemLine = (line) => {
    const trimmed = String(line || "").trim();
    if (!trimmed) return false;
    return (
      trimmed.startsWith("Current UTC time:") ||
      trimmed.startsWith("The user's timezone is ") ||
      trimmed ===
        "When scheduling messages at specific times, convert to the user's timezone offset in ISO-8601 format."
    );
  };

  const sanitizeSystemPromptForDisplay = (content) => {
    if (typeof content !== "string") return "";
    const lines = content.split(/\r?\n/);
    const filtered = lines.filter((line) => !isInternalRuntimeSystemLine(line));
    const output = [];
    let prevBlank = false;
    for (const line of filtered) {
      const blank = !String(line || "").trim();
      if (blank && prevBlank) continue;
      output.push(line);
      prevBlank = blank;
    }
    return output.join("\n").trim();
  };

  for (let i = 0; i < messages.length; i++) {
    const msg = messages[i];
    const msgKey = msg.messageId || msg.id || `idx-${i}`;
    const assistantName =
      msg.sourceAgentName || msg.agentName || defaultAgentName || "Agent";
    const assistantId =
      msg.sourceAgentId || msg.agentId || defaultAgentId || null;
    const assistantSenderKey = assistantId || assistantName || "__assistant__";
    const userName = publicUserDisplayName
      ? publicUserDisplayName
      : forceUserNameAsYou
        ? "You"
        : msg.senderDisplayName || msg.senderName || "You";
    const userSenderKey =
      msg.senderAgentId ||
      msg.senderUserId ||
      msg.senderId ||
      userName ||
      "__user__";

    if (msg.role === "system") {
      const humanTakeoverMarker = parseHumanTakeoverMarker(msg.content);
      if (humanTakeoverMarker) {
        maybeInsertDateDivider(msg.timestamp, `takeover-${msgKey}`);
        rendered.push(
          <HumanTakeoverMarker
            key={`msg-${msgKey}`}
            marker={humanTakeoverMarker}
            timestamp={msg.timestamp}
          />
        );
        prevRole = "system";
        prevSenderKey = null;
        continue;
      }
      const contextCompactedMarker = parseContextCompactedMarker(msg.content);
      if (contextCompactedMarker) {
        maybeInsertDateDivider(msg.timestamp, `compacted-${msgKey}`);
        rendered.push(
          <ContextCompactedMarker
            key={`msg-${msgKey}`}
            marker={contextCompactedMarker}
            timestamp={msg.timestamp}
          />
        );
        prevRole = "system";
        prevSenderKey = null;
        continue;
      }
      if (
        msg.messageChannel === "FORM_SUBMISSION" ||
        msg.messageChannel === "REVIEW_SUBMISSION"
      ) {
        maybeInsertDateDivider(msg.timestamp, `form-${msgKey}`);
        rendered.push(
          <FormSubmissionMessage
            key={`msg-${msgKey}`}
            content={msg.content}
            timestamp={msg.timestamp}
            senderName={msg.senderName || msg.senderDisplayName}
            isReview={msg.messageChannel === "REVIEW_SUBMISSION"}
          />
        );
        prevRole = "system";
        prevSenderKey = null;
        continue;
      }
      if (!showSystemPromptInChat) {
        continue;
      }
      const systemContent = sanitizeSystemPromptForDisplay(msg.content);
      if (!systemContent) {
        continue;
      }
      rendered.push(<SystemMessage key={`msg-${msgKey}`} content={systemContent} />);
      prevRole = "system";
      prevSenderKey = null;
    } else if (msg.role === "user") {
      maybeInsertDateDivider(msg.timestamp, msgKey);
      if (msg.senderAgentId) {
        if (!showInterAgentMessagesInChat) {
          continue;
        }
        rendered.push(
          <InterAgentMessage
            key={`msg-${msgKey}`}
            content={msg.content}
            timestamp={msg.timestamp}
            senderAgentId={msg.senderAgentId}
            senderAgentName={msg.senderAgentName}
            senderAgentSessionId={msg.senderAgentSessionId}
            responseRequirement={msg.responseRequirement}
            correlationId={msg.correlationId}
            onOpenAgentSettings={onOpenAgentSettings}
          />
        );
        prevRole = "user";
        prevSenderKey =
          msg.senderAgentId || msg.senderAgentName || "__agent_user__";
      } else if (useWorkflowProvenanceCards && !firstUserMessageSeen) {
        firstUserMessageSeen = true;
        rendered.push(
          <WorkflowMessage
            key={`msg-${msgKey}`}
            content={msg.content}
            timestamp={msg.timestamp}
            projectId={session?.projectId}
            projectName={session?.projectName}
            workflowSessionId={session?.workflowSessionId}
          />
        );
        prevRole = "user";
        prevSenderKey = userSenderKey;
      } else {
        const grouped = prevRole === "user" && userSenderKey === prevSenderKey;
        const effectiveMessageChannel = msg.messageChannel || null;
        rendered.push(
          <UserMessage
            key={`msg-${msgKey}`}
            content={msg.content}
            timestamp={msg.timestamp}
            grouped={grouped}
            senderName={userName}
            messageChannel={effectiveMessageChannel}
            imageUrls={msg.imageUrls}
          />
        );
        prevRole = "user";
        prevSenderKey = userSenderKey;
      }
    } else if (msg.role === "assistant" && Array.isArray(msg.parts) && msg.parts.length > 0) {
      maybeInsertDateDivider(msg.timestamp || msg.createdAt, msgKey);
      const grouped =
        (prevRole === "assistant" || prevRole === "tool") &&
        prevSenderKey === assistantSenderKey;
      const partElements = renderTypedAssistantParts(
        msg,
        `msg-${msgKey}`,
        assistantName,
        assistantId,
        grouped,
        onOpenAgentSettings,
        onOpenDynamicForm,
        onOpenCreatedFile,
        showToolCallsAndOutputsInChat,
        showSchedulingActivityInChat,
        showInterAgentMessagesInChat,
        respondedCorrelationIds,
        scheduledMessages,
        onEditScheduledMessage,
        onCancelScheduledMessage,
        onRefreshScheduledMessages,
        compactToolCalls,
      );
      rendered.push(...partElements);
      if (partElements.length > 0) {
        prevRole = "assistant";
        prevSenderKey = assistantSenderKey;
      }
    } else if (msg.role === "assistant" && msg.content) {
      maybeInsertDateDivider(msg.timestamp, msgKey);
      const grouped =
        (prevRole === "assistant" || prevRole === "tool") &&
        prevSenderKey === assistantSenderKey;
      const supervisorDecision = parseSupervisorDecisionContent(msg.content);
      if (supervisorDecision && !showSupervisorControlMessagesInChat) {
        const takeoverResponse = supervisorDecision.takeoverResponse?.trim();
        if (takeoverResponse) {
          rendered.push(
            <AgentMessage
              key={`msg-${msgKey}`}
              content={takeoverResponse}
              agentName={assistantName}
              agentId={assistantId}
              timestamp={msg.timestamp}
              grouped={grouped}
              onOpenAgentSettings={onOpenAgentSettings}
            />
          );
        } else {
          continue;
        }
      } else if (useWorkflowProvenanceCards) {
        rendered.push(
          <WorkflowResponseMessage
            key={`msg-${msgKey}`}
            content={msg.content}
            agentName={assistantName}
            agentId={assistantId}
            timestamp={msg.timestamp}
            grouped={grouped}
            projectId={session?.projectId}
            projectName={session?.projectName}
            workflowSessionId={session?.workflowSessionId}
            onOpenAgentSettings={onOpenAgentSettings}
          />
        );
      } else if (supervisorDecision) {
        rendered.push(
          <SupervisorDecisionMessage
            key={`msg-${msgKey}`}
            content={msg.content}
            agentName={assistantName}
            agentId={assistantId}
            timestamp={msg.timestamp}
            onOpenAgentSettings={onOpenAgentSettings}
          />
        );
      } else {
        rendered.push(
          <AgentMessage
            key={`msg-${msgKey}`}
            content={msg.content}
            agentName={assistantName}
            agentId={assistantId}
            timestamp={msg.timestamp}
            grouped={grouped}
            onOpenAgentSettings={onOpenAgentSettings}
          />
        );
      }
      prevRole = "assistant";
      prevSenderKey = assistantSenderKey;
    }
  }

  const needsThinking = chatStatus === 'submitted' || chatStatus === 'streaming';
  if (needsThinking) {
    rendered.push(
      <ThinkingIndicator
        key="__thinking__"
        agentName={defaultAgentName || "Agent"}
        agentId={defaultAgentId}
        onOpenAgentSettings={onOpenAgentSettings}
        label={streamToolLabel}
      />
    );
  }

  return { rendered, lastRole: prevRole, lastSenderKey: prevSenderKey, isShowingThinking: needsThinking };
}
